// CourseraSocialOutreach.js
// Coursera-branded manual-assist Social Outreach (no bots, no scraping)
// Self-contained styles (no external CSS).

import React, { useEffect, useMemo, useState } from "react";

/* ============================ Brand tokens & CSS ============================ */
const TOK = {
  ink: "#0B1A3C",
  ink70: "#0B1A3CB3",
  line: "#E6EDF2",
  bg: "#F4F6FA",
  card: "#FFFFFF",
  blue: "#2A73CC",
  blue600: "#1f62b2",
  navy600: "#0E2B57",
  pill: "#E9F2FF",
  pillInk: "#0B3A84",
  shadow: "0 10px 30px rgba(10,22,50,.12)",
  radius: 14,
};

const LOGO_SVG =
  "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/logos/Coursera%20-%20Blue.svg";
const LOGO_PNG =
  "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/logos/Coursera%20-%20Blue.png";

const HERO_THUMB =
  "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/thumbnails/sdrs_manualassist.png";

/* Conduit motif (SVG) — light, unobtrusive curves */
function ConduitBG({ style }) {
  return (
    <svg
      viewBox="0 0 1200 300"
      preserveAspectRatio="xMidYMid slice"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        opacity: 0.18,
        ...style,
      }}
      aria-hidden
    >
      <defs>
        <linearGradient id="g" x1="0" x2="1" y1="0" y2="1">
          <stop stopColor="#fff" stopOpacity="0.12" offset="0" />
          <stop stopColor="#fff" stopOpacity="0.0" offset="1" />
        </linearGradient>
      </defs>
      <path
        d="M0,250 C280,260 470,120 640,160 C820,202 980,290 1200,210"
        fill="none"
        stroke="#8DB9FF"
        strokeWidth="4"
        strokeLinecap="round"
      />
      <path
        d="M0,300 C260,280 430,130 700,160 C910,183 1020,240 1200,230"
        fill="none"
        stroke="url(#g)"
        strokeWidth="40"
        strokeLinecap="round"
      />
    </svg>
  );
}

/* Inline CSS (scoped by class names used in this component) */
const CSS = `
:root{--ink:${TOK.ink};--ink70:${TOK.ink70};--line:${TOK.line};--bg:${TOK.bg};--card:${TOK.card};--blue:${TOK.blue};--blue600:${TOK.blue600}}
*{box-sizing:border-box}
.app{background:var(--bg);min-height:100vh; font-family:"Source Sans Pro", system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif}
.page-head{max-width:1120px;margin:0 auto;padding:18px 20px 0;display:flex;gap:12px;align-items:center;color:var(--ink)}
.h1{font-size:28px;line-height:32px;font-weight:800;margin:0}
.kicker{margin-left:auto;color:var(--ink70)}

.hero{
  position:relative; isolation:isolate;
  max-width:1120px; margin:8px auto 18px; padding:18px 20px;
  border-radius:${TOK.radius}px;
  background: radial-gradient(800px 320px at 120% -40%, rgba(255,255,255,.16), transparent 60%),
              linear-gradient(180deg, var(--blue) 0%, var(--blue600) 100%);
  color:#fff; box-shadow:${TOK.shadow};
  overflow:hidden;
}
.hero__grid{display:grid; grid-template-columns:1.2fr .8fr; gap:16px; align-items:center}
.hero__title{margin:0;font-weight:900;font-size:28px;line-height:34px;letter-spacing:.2px}
.hero__sub{margin:6px 0 0; opacity:.95}
.hero__card{background:#fff;border:1px solid var(--line);border-radius:12px;box-shadow:0 8px 28px rgba(10,22,50,.18);padding:8px}

.brand-row{display:flex;align-items:center;gap:10px;margin-bottom:6px}
.brand-row .fb{opacity:.9;font-weight:700}

.wrap{max-width:1120px;margin:0 auto;padding:0 20px 24px}
.grid{display:grid;grid-template-columns:320px 1fr;gap:16px}
.card{background:var(--card);border:1px solid var(--line);border-radius:${TOK.radius}px;box-shadow:${TOK.shadow};padding:14px}
.card-header{display:flex;justify-content:space-between;align-items:center;margin-bottom:8px}
.sub{color:#5B6B83;font-size:12px}

.group{display:flex;flex-direction:column;gap:12px;margin-bottom:8px}
.row{display:flex;gap:10px;align-items:center}
.col{display:flex;flex-direction:column;gap:8px}
.input, .select, .btn, .textarea{
  border:1px solid var(--line);border-radius:10px;padding:10px 12px;font-size:14px;color:var(--ink);
}
.input, .select{width:100%}
.textarea{width:100%;min-height:120px;resize:vertical}
.btn{background:#fff;cursor:pointer;font-weight:700}
.btn.primary{background:var(--blue);border-color:transparent;color:#fff}
.badge{display:inline-flex;align-items:center;gap:6px;padding:4px 10px;border-radius:999px;border:1px solid var(--line);background:#F8FBFF}

.queue-list{max-height:360px;overflow:auto;border-top:1px solid var(--line)}
.link{display:block;width:100%;text-align:left;padding:10px 12px;border-bottom:1px solid var(--line);background:#fff;cursor:pointer}
.link.active{background:#F4F7FF}
.family{font-weight:700}
.muted{color:#7386A6}

.brand-coach{
  display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin-top:8px
}
.coach-pill{
  display:flex;align-items:center;gap:8px;
  background:#F0F6FF;border:1px solid #E0EBFF;color:#0B3A84;
  border-radius:999px;padding:6px 10px;font-weight:700;cursor:pointer
}
.coach-pill:hover{filter:brightness(0.98)}
.small{font-size:12px;color:#5B6B83}
`;

/* =============================== Utilities ================================ */
function renderTemplate(tpl, row) {
  return tpl.replace(/\{\{(\w+)\}\}/g, (_, k) => row?.[k] ?? "");
}

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length);
  if (!lines.length) return [];
  const headers = lines[0].split(",").map((h) => h.trim());
  return lines.slice(1).map((line) => {
    const cols = line.match(/("([^"]|"")*"|[^,]+)/g) || [];
    const cleaned = cols.map((c) =>
      c.replace(/^"/, "").replace(/"$/, "").replace(/""/g, '"').trim()
    );
    const row = {};
    headers.forEach((h, i) => (row[h] = cleaned[i] ?? ""));
    row.status = row.status || "new";
    row.lastActionAt = row.lastActionAt || "";
    return row;
  });
}

/* ---------- Canonicalize incoming CSV rows (accept multiple schemas) ------- */
function lowerKeys(o = {}) {
  const out = {};
  Object.keys(o || {}).forEach((k) => (out[k.toLowerCase()] = o[k]));
  return out;
}

function combineName(raw) {
  const full = raw["full_name"] || raw["name"];
  if (full) return full;
  const first = raw["first_name"] || "";
  const last = raw["last_name"] || "";
  const n = `${first} ${last}`.trim();
  return n || "";
}

function pickProfileUrl(raw) {
  return (
    raw["profile_url"] ||
    raw["linkedin_url"] ||
    raw["profile"] ||
    raw["url"] ||
    ""
  );
}

/**
 * Normalize one CSV row into:
 * { name, title, company, profile_url, persona, segment, status }
 * Supports:
 *  - Social Comments export: full_name, title_role, company, profile_url, …
 *  - Native schema: name, title, company, profile_url, persona, segment, status
 */
function normalizeContact(row = {}) {
  const r = lowerKeys(row);

  const name = combineName(r);
  const title =
    r["title"] || r["title_role"] || r["role"] || r["job_title"] || "";
  const company = r["company"] || r["org"] || r["employer"] || "";
  const profile_url = pickProfileUrl(r);

  const persona = r["persona"] || "";
  const segment = r["segment"] || "";
  const status = r["status"] || "new";

  if (!name && !profile_url) return null;
  return { name, title, company, profile_url, persona, segment, status };
}

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {}
}

function downloadCSV(filename, rows) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const body = rows
    .map((r) =>
      headers
        .map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`)
        .join(",")
    )
    .join("\n");
  const csv = [headers.join(","), body].join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function StatusBadge({ status }) {
  const map = {
    new: { label: "new", color: "#93C5FD" },
    requested: { label: "requested", color: "#7DD3FC" },
    connected: { label: "connected", color: "#A78BFA" },
    messaged: { label: "messaged", color: "#FBBF24" },
  };
  const s = map[status] || map.new;
  return (
    <span className="badge" style={{ background: s.color, color: "#0f172a" }}>
      {s.label}
    </span>
  );
}

/* =============================== Component ================================ */
const LS_KEY = "coursera-social-outreach-v1";

export default function CourseraSocialOutreach() {
  const [prospects, setProspects] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const [connectNoteTpl, setConnectNoteTpl] = useState(
    "Hi {{name}}, great to see what you’re building at {{company}}. I partner with {{title}} leaders on learning/skills initiatives—exploring new ways to improve {{product / need / fit}}. Worth connecting?"
  );
  const [followupTpl, setFollowupTpl] = useState(
    "Appreciate the connection, {{name}}! Would you be open to a short note with insights we created for {{persona}} in {{segment}}—or should I send a brief Zoom link?"
  );

  const [dailyLimit, setDailyLimit] = useState(50);
  const [sentToday, setSentToday] = useState(0);
  const [todayStr, setTodayStr] = useState(() => new Date().toDateString());

  const [log, setLog] = useState([]);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  // Load
  useEffect(() => {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return;
    try {
      const s = JSON.parse(raw);
      setProspects(s.prospects ?? []);
      setSelectedIndex(s.selectedIndex ?? 0);
      setConnectNoteTpl(s.connectNoteTpl ?? connectNoteTpl);
      setFollowupTpl(s.followupTpl ?? followupTpl);
      setDailyLimit(s.dailyLimit ?? dailyLimit);
      setSentToday(s.sentToday ?? sentToday);
      setTodayStr(s.todayStr ?? todayStr);
      setLog(s.log ?? []);
    } catch {}
    // eslint-disable-next-line
  }, []);

  // Save
  useEffect(() => {
    localStorage.setItem(
      LS_KEY,
      JSON.stringify({
        prospects,
        selectedIndex,
        connectNoteTpl,
        followupTpl,
        dailyLimit,
        sentToday,
        todayStr,
        log,
      })
    );
  }, [
    prospects,
    selectedIndex,
    connectNoteTpl,
    followupTpl,
    dailyLimit,
    sentToday,
    todayStr,
    log,
  ]);

  // Reset daily counter
  useEffect(() => {
    const now = new Date().toDateString();
    if (now !== todayStr) {
      setTodayStr(now);
      setSentToday(0);
    }
  }, [todayStr]);

  const current = prospects[selectedIndex];

  // Filter
  const filteredProspects = useMemo(() => {
    const q = search.trim().toLowerCase();
    return prospects.filter((p) => {
      const matchesQ =
        !q ||
        [p.name, p.title, p.company, p.persona, p.segment]
          .filter(Boolean)
          .some((x) => String(x).toLowerCase().includes(q));
      const matchesStatus = filterStatus === "all" || p.status === filterStatus;
      return matchesQ && matchesStatus;
    });
  }, [prospects, search, filterStatus]);

  // keep selection valid
  useEffect(() => {
    if (!filteredProspects.length) return;
    const idxInFiltered = filteredProspects.indexOf(current);
    if (idxInFiltered === -1) {
      setSelectedIndex(
        Math.min(selectedIndex, Math.max(0, filteredProspects.length - 1))
      );
    }
    // eslint-disable-next-line
  }, [filteredProspects.length]);

  const connectPreview = useMemo(
    () => (current ? renderTemplate(connectNoteTpl, current) : ""),
    [current, connectNoteTpl]
  );
  const followupPreview = useMemo(
    () => (current ? renderTemplate(followupTpl, current) : ""),
    [current, followupTpl]
  );

  // Handlers
  function onUploadCSV(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      const rawRows = parseCSV(String(reader.result));
      const normalized = rawRows.map(normalizeContact).filter(Boolean);
      setProspects(normalized);
      setSelectedIndex(0);
    };
    reader.readAsText(f);
  }

  function openProfile(url) {
    window.open(url || "https://www.linkedin.com/", "_blank", "noopener");
  }

  function markAction(action, idx = selectedIndex) {
    const next = [...prospects];
    const p = { ...next[idx] };
    p.lastActionAt = new Date().toISOString();
    if (action === "connect") p.status = "requested";
    if (action === "message") p.status = "messaged";
    next[idx] = p;
    setProspects(next);
    setLog((l) => [
      { idx, action, name: p.name, company: p.company, at: p.lastActionAt },
      ...l,
    ]);
  }

  const nextProspect = () => {
    const list = filteredProspects;
    if (!list.length) return;
    const pos = list.indexOf(current);
    const target = list[Math.min(pos + 1, list.length - 1)];
    const globalIdx = prospects.indexOf(target);
    setSelectedIndex(globalIdx === -1 ? selectedIndex : globalIdx);
  };

  async function runConnect() {
    if (!current) return;
    if (sentToday >= dailyLimit) {
      alert("Daily limit reached.");
      return;
    }
    await copyToClipboard(connectPreview);
    openProfile(current.profile_url);
    markAction("connect");
    setSentToday((n) => n + 1);
  }

  async function runFollowup() {
    if (!current) return;
    await copyToClipboard(followupPreview);
    openProfile(current.profile_url);
    markAction("message");
  }

  function exportLog() {
    if (!log.length) return;
    downloadCSV("coursera_social_activity.csv", log);
  }

  // Brand coach: one-click inserts aligned to principles
  const coachInserts = {
    empathy:
      "Noticed your work on {{topic}} at {{company}} — curious what outcomes matter most for your {{team/function}} this quarter?",
    value:
      "We just published a short insight for {{persona}} in {{segment}} on {{theme}} — would a 90-sec summary help?",
    team: "If helpful, I can loop in our {{expert/function}} to co-review ideas — we’ve seen strong results in {{peer/story}}.",
    results:
      "Happy to share a quick before/after from a similar customer (anonymized) so you can judge fit in 2 slides.",
  };

  const insert = (which) =>
    setFollowupTpl((t) =>
      [t.trim(), coachInserts[which]].filter(Boolean).join("\n\n")
    );

  /* --------------------------------- UI ---------------------------------- */
  return (
    <div className="app">
      <style>{CSS}</style>

      {/* Top: tiny brand row */}
      <div className="page-head">
        <picture>
          <source srcSet={LOGO_SVG} type="image/svg+xml" />
          <img src={LOGO_PNG} alt="Coursera" height="18" />
        </picture>
        <h1 className="h1">for Business • Social Outreach</h1>
        <div className="kicker">
          Manual-assist outreach: you copy text and click inside social
          profiles. No bots.
        </div>
      </div>

      {/* Coursera Blue hero + Conduit */}
      <section className="hero" aria-label="Coursera hero">
        <ConduitBG />
        <div className="brand-row">
          <span className="fb">for Business</span>
        </div>
        <div className="hero__grid">
          <div>
            <h2 className="hero__title">Social outreach, the safe way</h2>
            <p className="hero__sub">
              Paste/export a CSV → work the queue → copy & open profiles. Your
              data stays in the browser. Built for ABM 1:1 & 1:Few.
            </p>

            {/* Brand coach pills */}
            <div className="brand-coach">
              <button className="coach-pill" onClick={() => insert("empathy")}>
                Build empathy
              </button>
              <button className="coach-pill" onClick={() => insert("value")}>
                Create value
              </button>
              <button className="coach-pill" onClick={() => insert("team")}>
                Leverage our team
              </button>
              <button className="coach-pill" onClick={() => insert("results")}>
                Demonstrate results
              </button>
            </div>
            <div className="small" style={{ marginTop: 6 }}>
              One-click inserts add a sentence to your follow-up that reflects a
              Coursera brand principle.
            </div>
          </div>
          {/* RIGHT: hero visual */}
          <div className="hero__card" aria-hidden>
            <img
              src={HERO_THUMB}
              alt="Social Outreach (manual-assist) thumbnail"
              style={{
                display: "block",
                width: "100%",
                height: "auto",
                borderRadius: 12,
              }}
            />
          </div>
        </div>
      </section>

      <div className="wrap">
        <div className="grid">
          {/* LEFT rail */}
          <div className="col">
            <div className="card">
              <h4 style={{ margin: 0 }}>Search &amp; Filter</h4>
              <div className="group">
                <div className="col">
                  <label>Search</label>
                  <input
                    className="input"
                    placeholder="name, title, company, persona, segment…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div className="col">
                  <label>Status</label>
                  <select
                    className="select"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <option value="all">All</option>
                    <option value="new">New</option>
                    <option value="requested">Requested</option>
                    <option value="connected">Connected</option>
                    <option value="messaged">Messaged</option>
                  </select>
                </div>
                <div className="col">
                  <label>Upload CSV</label>
                  <input
                    className="select"
                    type="file"
                    accept=".csv"
                    onChange={onUploadCSV}
                  />
                </div>
                <div className="col">
                  <label>Download Template</label>
                  <button
                    className="btn"
                    onClick={() =>
                      downloadCSV("coursera_social_template.csv", [
                        {
                          name: "Jane Doe",
                          title: "VP Engineering",
                          company: "Acme Inc",
                          profile_url:
                            "https://www.linkedin.com/in/janedoe-sample",
                          persona: "Engineering",
                          segment: "Enterprise",
                          status: "new",
                        },
                      ])
                    }
                  >
                    CSV Template
                  </button>
                </div>
              </div>
            </div>

            <div className="card">
              <h4 style={{ margin: 0 }}>Safety &amp; Pace</h4>
              <div className="group">
                <div className="row">
                  <label style={{ minWidth: 90 }}>Daily limit</label>
                  <input
                    className="input"
                    type="number"
                    min={1}
                    value={dailyLimit}
                    onChange={(e) =>
                      setDailyLimit(parseInt(e.target.value || 0))
                    }
                    style={{ maxWidth: 140 }}
                  />
                  <div className="small" style={{ marginLeft: "auto" }}>
                    <b>{sentToday}</b> sent today
                  </div>
                </div>
                <div className="row">
                  <label style={{ minWidth: 90 }}>Prospects</label>
                  <div className="small">{prospects.length}</div>
                </div>
                <div className="row">
                  <label style={{ minWidth: 90 }}>Activity log</label>
                  <button className="btn" onClick={exportLog}>
                    Export CSV
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT column */}
          <div className="col">
            {/* Queue */}
            <div className="card">
              <div className="card-header">
                <h3 style={{ margin: 0 }}>Queue</h3>
                <div className="sub">
                  Showing {filteredProspects.length} of {prospects.length}
                </div>
              </div>
              <div className="queue-list">
                {filteredProspects.length ? (
                  filteredProspects.map((p, i) => {
                    const globalIdx = prospects.indexOf(p);
                    const active = globalIdx === selectedIndex;
                    return (
                      <button
                        key={globalIdx + (p.profile_url || i)}
                        className={`link${active ? " active" : ""}`}
                        onClick={() => setSelectedIndex(globalIdx)}
                        title={p.profile_url}
                      >
                        <div
                          className="row"
                          style={{ justifyContent: "space-between" }}
                        >
                          <div className="col" style={{ gap: 2 }}>
                            <div className="family">
                              {p.name || "(no name)"}
                            </div>
                            <div className="muted small">
                              {p.title} · {p.company}
                            </div>
                          </div>
                          <StatusBadge status={p.status} />
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <div
                    className="small"
                    style={{ padding: 12, lineHeight: 1.5 }}
                  >
                    Upload a CSV with either:
                    <br />
                    <code>
                      name,title,company,profile_url[,persona,segment]
                    </code>
                    <br />
                    or the Social Comments export:
                    <br />
                    <code>full_name,title_role,company,profile_url,…</code>
                  </div>
                )}
              </div>
            </div>

            {/* Workbench */}
            <div className="card">
              <div className="card-header">
                <h3 style={{ margin: 0 }}>Workbench</h3>
                {current && (
                  <a
                    className="sub"
                    href={current.profile_url || "#"}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open profile ↗
                  </a>
                )}
              </div>

              {current ? (
                <div
                  style={{ maxHeight: 520, overflow: "auto", paddingRight: 2 }}
                >
                  <div
                    className="row"
                    style={{ justifyContent: "space-between" }}
                  >
                    <div className="col">
                      <div className="family">
                        {current.name || "(no name)"}
                      </div>
                      <div className="muted small">
                        {current.title} · {current.company}
                      </div>
                      <div className="muted small">
                        {current.persona} · {current.segment}
                      </div>
                    </div>
                    <StatusBadge status={current.status} />
                  </div>

                  <div className="group" style={{ marginTop: 10 }}>
                    <h4 style={{ margin: 0 }}>
                      Connection note (you click send)
                    </h4>
                    <textarea
                      className="textarea"
                      value={connectPreview}
                      readOnly
                    />
                    <div className="row">
                      <button className="btn primary" onClick={runConnect}>
                        Copy & Open profile
                      </button>
                      <button
                        className="btn"
                        onClick={() => {
                          copyToClipboard(connectPreview);
                          markAction("connect");
                        }}
                      >
                        Copy Only
                      </button>
                      <button className="btn" onClick={nextProspect}>
                        Next →
                      </button>
                    </div>
                  </div>

                  <div className="group">
                    <h4 style={{ margin: 0 }}>Follow-up (after acceptance)</h4>
                    <textarea
                      className="textarea"
                      value={followupPreview}
                      readOnly
                    />
                    <div className="row">
                      <button className="btn primary" onClick={runFollowup}>
                        Copy & Open profile
                      </button>
                      <button
                        className="btn"
                        onClick={() => {
                          copyToClipboard(followupPreview);
                          markAction("message");
                        }}
                      >
                        Copy Only
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="small muted">No prospect selected.</div>
              )}
            </div>

            {/* Templates */}
            <div className="card">
              <div className="card-header">
                <h3 style={{ margin: 0 }}>Templates & Personalization</h3>
                <div className="sub">
                  Use variables: <code>{"{{name}}"}</code>,{" "}
                  <code>{"{{title}}"}</code>, <code>{"{{company}}"}</code>,{" "}
                  <code>{"{{persona}}"}</code>, <code>{"{{segment}}"}</code>
                </div>
              </div>

              <div className="row" style={{ gap: 12 }}>
                <div className="col" style={{ flex: 1 }}>
                  <small className="muted">Connection Note</small>
                  <textarea
                    className="textarea"
                    value={connectNoteTpl}
                    onChange={(e) => setConnectNoteTpl(e.target.value)}
                  />
                </div>
                <div className="col" style={{ flex: 1 }}>
                  <small className="muted">Follow-up Message</small>
                  <textarea
                    className="textarea"
                    value={followupTpl}
                    onChange={(e) => setFollowupTpl(e.target.value)}
                  />
                </div>
              </div>

              <details style={{ marginTop: 6 }}>
                <summary>Guidance</summary>
                <ul style={{ margin: "6px 0 0 18px" }}>
                  <li>Manual-assist only. You perform the final click.</li>
                  <li>Keep first notes short; personalize by title/company.</li>
                  <li>Stay under sensible daily caps (configured at left).</li>
                </ul>
              </details>
            </div>

            {/* Activity */}
            {log.length > 0 && (
              <div className="card">
                <h3 style={{ marginTop: 0 }}>Recent Activity</h3>
                <div className="sub">Most recent first</div>
                <div style={{ maxHeight: 240, overflow: "auto", marginTop: 8 }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "separate",
                      borderSpacing: 0,
                      border: `1px solid ${TOK.line}`,
                      borderRadius: 12,
                      overflow: "hidden",
                    }}
                  >
                    <thead>
                      <tr style={{ background: "#F8FAFE" }}>
                        {["Name", "Company", "Action", "Timestamp"].map((h) => (
                          <th
                            key={h}
                            style={{
                              textAlign: "left",
                              padding: "10px 12px",
                              borderBottom: `1px solid ${TOK.line}`,
                              fontWeight: 700,
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {log.map((r, i) => (
                        <tr key={i}>
                          <td style={{ padding: "10px 12px" }}>{r.name}</td>
                          <td style={{ padding: "10px 12px" }}>{r.company}</td>
                          <td style={{ padding: "10px 12px" }}>{r.action}</td>
                          <td style={{ padding: "10px 12px" }}>
                            {new Date(r.at).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
